import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import equal from 'fast-deep-equal/react';
import * as React from 'react';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { useDeleteSavedSearchMutation, useSavedSearchesQuery } from '../../../../queries/ContactSearch';
import { ContactsGutterTag } from '../../contacts/ContactsGutterTag';
import { SearchIcon } from '../../svgs/icons/SearchIcon';
import { brandPrimaryHover, white } from '../../../styles/colors';
import { PeopleCollapsibleSection } from '../../../containers/PeopleV2/PeopleContainer/PeopleCollapsibleSection';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
	currentFilter?: Api.IBulkContactsRequest;
	onSelectSearch: (filter: Api.IBulkContactsRequest) => void;
	readOnly?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	selectedSearchId?: string;
	onSearchSelected?: (searchId: string) => void;
	showRadioButtons?: boolean;
}

export const SavedSearchesList: React.FC<IProps> = ({
	className,
	styles,
	currentFilter,
	onSelectSearch,
	readOnly = false,
	impersonationContext,
	selectedSearchId,
	onSearchSelected,
	showRadioButtons = false,
}) => {
	const { pushApiError } = useErrorMessages();
	const { logApiError } = useEventLogging();

	const { data: searches = [], isLoading } = useSavedSearchesQuery({
		meta: {
			toastErrorMessage: 'Error fetching saved searches',
			errorLogging: {
				category: 'SavedSearches',
				action: 'LoadSavedSearches-Error',
			},
		},
	});

	const deleteMutation = useDeleteSavedSearchMutation({
		onSuccess: () => {
			toast.success('Search was deleted');
		},
		onError: (err: unknown) => {
			const apiError = err as Api.IOperationResultNoValue;
			pushApiError(apiError);
			logApiError('DeleteSavedSearch-Error', apiError);
		},
	});

	const onDeleteSearch = React.useCallback(
		(search: Api.ContactSearch) => () => {
			if (readOnly) return;
			deleteMutation.mutate({ searchId: search.id, impersonationContext });
		},
		[deleteMutation, impersonationContext, readOnly]
	);

	const handleSelectSearch = React.useCallback(
		(search: Api.ContactSearch) => () => {
			if (showRadioButtons && onSearchSelected) {
				onSearchSelected(search.id);
			}
			onSelectSearch({ filter: search.filter, ownershipFilter: search.ownershipFilter });
		},
		[onSelectSearch, onSearchSelected, showRadioButtons]
	);

	const isMatch = React.useCallback(
		(search: Api.ContactSearch) => {
			if (showRadioButtons && selectedSearchId) {
				return search.id === selectedSearchId;
			}
			return currentFilter ? equal(currentFilter.filter, search.filter) : false;
		},
		[currentFilter, selectedSearchId, showRadioButtons]
	);

	if (!searches.length && !isLoading) {
		return (
			<div className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
				<div className={css(styleSheet.emptyText)}>0 saved searches</div>
			</div>
		);
	}

	const renderSearches = () => {
		return searches.map(search => {
			const isSelected = isMatch(search);
			return (
				<ContactsGutterTag
					className={css(styleSheet.search)}
					key={`savedsearcheslist-tag-${search.id}`}
					isActive={isSelected}
					leftAccessory={
						<SearchIcon
							fillColor={isSelected ? white : brandPrimaryHover}
							viewBox='-1 -1 18 18'
							strokeWidth={0.5}
							className={css(styleSheet.searchIcon)}
						/>
					}
					onRemoveButtonClicked={onDeleteSearch(search)}
					showRemoveButton={!readOnly}
					updateSelectedTagList={handleSelectSearch(search)}
					tag={search.name}
					hideCheckbox={!showRadioButtons}
					useRadioButton={showRadioButtons}
				/>
			);
		});
	};

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
			<PeopleCollapsibleSection itemHeightPx={34} items={renderSearches()} showLoader={isLoading} />
		</div>
	);
};
